import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Controller, Form, useForm } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { validateEmail } from 'imports/core/api/validators';
import Flex from 'imports/core/ui/atoms/Flex';
import { useSearchParams } from 'next/navigation';
import Link from '/components/Link';
import { withConfirm } from '/imports/core/api/confirm';
import { useResponsive } from '/imports/core/api/responsiveContext';
import useIntl from '/imports/core/api/useIntl';
import useTracking from '/imports/core/hooks/useTracking';
import ValidatedInput from '/imports/core/ui/atoms/ValidatedInput';
import CancelSubscriptionNotFound from '/imports/core/ui/components/CancelSubscriptionNotFound';
import CancelSubscriptionRequest from '/imports/core/ui/components/CancelSubscriptionRequest';
import ContactFormStatusMessage from '/imports/core/ui/components/ContactFormStatusMessage.js';
import { replaceString } from '/imports/core/ui/helpers';
import FieldLabel from '/imports/generator/ui/atoms/FieldLabel';
import { getDomainName, getRoute, isBlogDomain, sendDowngradeEmail } from '/lib/helpers';

const CancelSubscriptionForm = ({ confirm }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    getFieldState,
    getValues,
    watch,
  } = useForm({
    mode: 'all',
  });
  const { t, locale } = useIntl();
  const [status, setStatus] = useState({
    sending: false,
    success: false,
    error: null,
  });
  const { trackEvent } = useTracking();
  const { host } = useResponsive();
  const cancel_success = replaceString(t('contact.cancel_success'), { Resumedone: getDomainName(host) });
  const emailValue = watch('email');
  const queryParams = useSearchParams();

  useEffect(() => {
    if (status.error && emailValue) {
      setStatus({ ...status, error: null });
    }
  }, [emailValue]);

  const cancelSubscription = async (data) => {
    setStatus({
      sending: false,
      success: false,
      error: null,
    });
    confirm();
    const email = data.email.toLowerCase();
    try {
      await sendDowngradeEmail(email, locale, host);
      trackEvent('cancel_sub_requested', { email });
      setStatus({ ...status, success: true });
    } catch (e) {
      setStatus({ ...status, sending: false, error: e.message });
    }
  };

  const onSubmit = (data) => {
    confirm({
      title: t('contact_cancel_subscription_title'),
      text: t('contact_cancel_subscription_text'),
      confirmText: t('contact.yes'),
      onConfirm() {
        cancelSubscription(data);
      },
    });
  };

  const { sending, success, error } = status;

  const email = queryParams && queryParams.get('email') ? queryParams && queryParams.get('email') : getValues('email');

  if (success || (queryParams && queryParams.get('success'))) return <CancelSubscriptionRequest email={email} />;

  if (error === 'subscription not found' || (queryParams && queryParams.get('error')))
    return <CancelSubscriptionNotFound notSubscribed />;

  const disabled = sending;

  const refundPolicy = replaceString(t('contact.learn_about_policy'), {
    '/refund-cancellation-policy': getRoute('/refund-cancellation-policy', locale, false, host, isBlogDomain(host)),
  });

  return (
    <MainWrapper $fullWidth direction="column">
      <FormWrapperSection $fullWidth direction="column">
        <HeadingWrap direction="column" $fullWidth>
          <MainTitle>{t('contact.cancelling_subscription')}</MainTitle>
          <SubTitle>{cancel_success}</SubTitle>
        </HeadingWrap>
        <InfoWrap direction="column">
          <Title>{t('contact_cancel_subscription_form')}</Title>
          <Info>{t('contact.cancel_subscription_heading')}</Info>
        </InfoWrap>
        <Forms onSubmit={handleSubmit(onSubmit)} control={control}>
          <InputWrap>
            <FieldLabel label={t('email')} background="transparent" summary></FieldLabel>
            <Controller
              name="email"
              control={control}
              defaultValue={''}
              rules={{ validate: validateEmail }}
              render={({ field }) => (
                <Input
                  type="email"
                  {...field}
                  isValid={getFieldState('email').invalid}
                  disableShadow
                  placeholder={t('contact.your_email_here')}
                  errorMessageCustomCss={t(errors?.email?.message)}
                  isErrorthere={status.error != null}
                />
              )}
            />
          </InputWrap>
          {error && getValues('email') && <ContactFormStatusMessage error={error} message={error} t={t} $nopadding />}
          <Button type="submit" disabled={error || disabled || getFieldState('email').invalid}>
            {t('contact_cancel_subscription_title')}
          </Button>
        </Forms>
        <InfoWrap direction="column">
          <InfoWrapper>{t('contact_cancel_subscription_info')}</InfoWrapper>
          <InfoWrapper>
            {t('contact.cancel_subscription_problem_info')}{' '}
            <Link href="/contact-us">{t('contact.contact_support')}</Link>
          </InfoWrapper>
          <InfoWrapper>
            <span dangerouslySetInnerHTML={{ __html: refundPolicy }} />
          </InfoWrapper>
        </InfoWrap>
      </FormWrapperSection>
    </MainWrapper>
  );
};

CancelSubscriptionForm.propTypes = {
  confirm: PropTypes.func,
};

const InfoWrap = styled(Flex)`
  gap: 16px;
`;

const HeadingWrap = styled(Flex)`
  gap: 16px;
`;

const SubTitle = styled.div`
  color: #7171a6;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 20px;
  line-height: 26px;
`;

const MainTitle = styled.div`
  color: rgba(22, 136, 254, 1);
  font-family: ${({ theme }) => theme.font.family.websiteBold};
  font-size: 40px;
  line-height: 50px;
`;

const FormWrapperSection = styled(Flex)`
  gap: 32px;
  max-width: 608px;
  margin: 0 auto;
  width: 90%;
  padding: 20px 0;

  @media (max-width: 565px) {
    width: 100%;
    margin: 0;
    padding: 40px 20px;
  }
`;

const MainWrapper = styled(Flex)`
  width: 100%;
  min-height: calc(100vh - 182px);
  justify-content: center;
  background-color: #f1f0f2;

  @media (max-width: 980px) {
    margin-top: 74px;
  }
  @media (max-width: 565px) {
    min-height: unset;
  }
`;

const Title = styled.div`
  color: #282b32;
  font-family: ${({ theme }) => theme.font.family.websiteBold};
  font-size: 24px;
  line-height: 32px;

  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}
`;

const Info = styled.div`
  color: #14141f;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 18px;
  line-height: 24px;

  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}
`;

const Forms = styled(Form)`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 16px;

  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      justify-content: flex-start;
      direction: rtl;
    `}
`;

const InputWrap = styled.div`
  position: relative;
  margin-top: 8px;
  label {
    top: -10px;
  }

  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}
`;

const Input = styled(ValidatedInput)`
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  color: #595964;
  font-size: 15px;
  line-height: 22px;
  height: 48px;
  padding: 8px 12px;
  background: var(--light-values-white);
  border-radius: 4px;
  border: 1px solid #e3e3e4;

  ${({ isErrorthere }) =>
    isErrorthere &&
    css`
      border: 1px solid #f00;
    `}
`;

const Button = styled.button`
  border-radius: 4px;
  background: #1688fe;
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
  color: #fff;
  font-size: 18px;
  line-height: 24px;
  cursor: pointer;
  height: 48px;
  border: none;
  transition: all 0.2s;

  &:disabled {
    background: #c5c5e933;
    border-color: #c5c5e933;
    cursor: default;
    color: #d8d8f3;
  }
`;

const InfoWrapper = styled.div`
  color: #14141f;
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 16px;
  line-height: 24px;

  a {
    color: #1688fe;
    font-size: 16px;
    cursor: pointer;
    text-decoration: none;
  }

  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      direction: rtl;
    `}
`;

export default withConfirm(CancelSubscriptionForm);
